import React from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import {
    getAdditionalFlags,
    getAdditionalFlagValuesFromStorage,
    getCommaSeparatedCurrencyValue,
    getCommaSeparatedNumericValue,
    getFullStringCondition
} from 'Utils';
import { INPUT_PARAMETER_TYPES } from 'Common/enums';
import { GetLandlordInfo } from 'Services';
import useGetLandlordInfo from 'Hooks/query-hooks/tenant/useGetLandlordInfo';

import styles from './RuleStatementOverview.css';

type Props = {
    expression?: AdvancedRuleExpression;
    ruleInputs?: RuleInputs[];
    lists: RuleLists[];
    parameters: Rule['parameters'];
    outputDescription: string;
    listData?: ListManagementApiList[];
    conclusionValues?: ConclusionValues;
};

const getListNamesSeparatedBySemiColon = (
    currentRuleLists: RuleLists[],
    listEntries?: ListManagementApiList[],
    listIndex?: number
) => {
    const invalidListIndex = listIndex == null || _.isNaN(listIndex) || _.isUndefined(listIndex);

    if (invalidListIndex || !listEntries?.length) return '';

    const strListIds = currentRuleLists[Number(listIndex)].listIds.map((id: number) => id.toString());
    const listNames: (string | undefined)[] = strListIds.map(
        (id: string) => listEntries.find((list) => list.id.toString() === id)?.name
    );

    if (listNames.every((entry) => entry === undefined)) {
        return '';
    }
    return listNames.join('; ');
};

const getFormattedEditableFieldValue = (
    t: TFunction,
    input: RuleInputs,
    currentRuleLists: RuleLists[],
    parameters: Rule['parameters'],
    listEntries?: ListManagementApiList[],
    dataLandlordInfo?: GetLandlordInfo
) => {
    /**
     * Templated Rules (TR) using External List and clients using TR will not be a Tenant with Landlord.
     * If the client will use adhoc (simple or advanced) rules Templated rules will be turned off.
     */
    const placeholder = dataLandlordInfo?.tenantWithLandlord ? t('Network List') : t('External List');
    let fieldValue: string = parameters[input.parameter.name];
    switch (input.parameter.type) {
        case INPUT_PARAMETER_TYPES.LIST:
            fieldValue = getListNamesSeparatedBySemiColon(currentRuleLists, listEntries, input.parameter.listIndex);
            fieldValue = fieldValue === '' ? placeholder : fieldValue;
            break;
        case INPUT_PARAMETER_TYPES.INTEGER:
            fieldValue = getCommaSeparatedNumericValue(parameters[input.parameter.name]);
            break;
        case INPUT_PARAMETER_TYPES.DECIMAL:
            fieldValue = getCommaSeparatedCurrencyValue(parameters[input.parameter.name]);
            break;
        default:
            break;
    }
    return `[ ${fieldValue} ]`;
};

const RuleStatementOverview = ({
    outputDescription,
    ruleInputs,
    parameters,
    lists,
    listData,
    expression,
    conclusionValues
}: Props) => {
    const { t } = useTranslation();

    const { data: dataLandlordInfo, isLoading: isLoadingLandlordInfo } = useGetLandlordInfo();
    if (isLoadingLandlordInfo) return null;

    const conclusionFlagListData: AdditionalConclusionValues[] = getAdditionalFlagValuesFromStorage();
    const setFlags = conclusionValues?.setFlags?.map((action) => {
        const flags = getAdditionalFlags(conclusionFlagListData, action.values as string[]);
        return (
            <React.Fragment key={_.uniqueId()}>
                {`${t('And Set Flags')} [${flags.join(', ')}]`}
                <br />
            </React.Fragment>
        );
    });

    const addToList = conclusionValues?.addToList?.map((action) => {
        const { listIds, message } = action.values as AddToList;
        return (
            <React.Fragment key={_.uniqueId()}>
                {`${t('And Add Parameter')} [${message}] ${t('to List')} [${listIds.join(', ')}]`}
                <br />
            </React.Fragment>
        );
    });

    if (expression)
        return (
            <div className={styles.row}>
                <div className={styles.ruleStatementText}>
                    <pre className={styles.brightScriptPre}>
                        <code>{expression.expText}</code>
                    </pre>
                </div>
            </div>
        );

    return (
        <div>
            <div className={styles.row}>
                <div className={styles.label}>{t('WHEN')}</div>
                <div className={styles.statmentTextWrapper}>
                    {ruleInputs?.map((input, index) => {
                        return (
                            <span key={`${input.toString()}+${index * 2}`} className={styles.statement}>
                                {index < 1 &&
                                    input.condition &&
                                    `${input.message} ${getFullStringCondition(t, input.condition)} `}
                                {index > 0 &&
                                    input.condition &&
                                    `${t('and')} ${input.message} ${getFullStringCondition(t, input.condition)} `}
                                {getFormattedEditableFieldValue(
                                    t,
                                    input,
                                    lists,
                                    parameters,
                                    listData,
                                    dataLandlordInfo
                                )}
                            </span>
                        );
                    })}
                </div>
            </div>

            <div className={styles.divider} />

            <div className={styles.row}>
                <div className={styles.label}>{t('THEN')}</div>
                <div className={styles.statmentTextWrapper}>
                    {`${outputDescription?.replace(
                        '[score]',
                        `[ ${conclusionValues?.updateScoreBy?.[0]?.values?.toString() || parameters.score} ]`
                    )}`}
                    <div>
                        {setFlags}
                        {addToList}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default React.memo(RuleStatementOverview);
